import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Calendar, Sparkles, Ship, MapPin, Package, Anchor, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { getTodayForInput, getMonthsAgoForInput, formatDisplayDate } from '../../utils/dateUtils';

// Sample shipment data
const SAMPLE_SHIPMENTS = [
  {
    id: 'SHIPZ02261176',
    vessel: 'MSC ADELE',
    origin: 'GUJAN-MESTRAS',
    destination: 'CHASNO',
    status: 'Pending',
    statusTone: 'bg-amber-50 text-amber-700 border border-amber-200',
    currentStep: 2,
    eta: '2024-03-15',
    lastUpdate: '2 hours ago',
  },
  {
    id: 'SHIPZ02261175',
    vessel: 'MSC ABIDJAN',
    origin: 'NEW YORK',
    destination: 'ROTTERDAM',
    status: 'On Time',
    statusTone: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    currentStep: 6,
    eta: '2024-03-10',
    lastUpdate: '30 mins ago',
  },
  {
    id: 'SHIPZ02261174',
    vessel: 'TESTING VESSEL',
    origin: 'TESERO',
    destination: 'TESERO',
    status: 'Delayed',
    statusTone: 'bg-rose-50 text-rose-700 border border-rose-200',
    currentStep: 3,
    eta: '2024-03-20',
    lastUpdate: '1 day ago',
  },
];

// Milestone steps
const STEPS = ['Booked', 'Docs Ready', 'Gate In', 'Loaded', 'Departed', 'In Transit', 'Arrived', 'Discharged', 'Delivered'];

// MilestoneTrack Component
const MilestoneTrack = ({ current }) => {
  return (
    <div className="grid grid-cols-9 gap-1">
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s} className="relative flex flex-col items-center">
            {/* Connecting line */}
            {i < STEPS.length - 1 && (
              <span
                aria-hidden
                className={[
                  'absolute top-3.5 left-[55%] right-[-45%] h-0.5 rounded-full transition-all duration-300',
                  i < current ? 'bg-emerald-500' :
                  i === current ? 'bg-gradient-to-r from-amber-500 to-slate-200' :
                  'bg-slate-200',
                ].join(' ')}
              />
            )}
            
            {/* Step circle */}
            <span
              className={[
                'relative w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all duration-300',
                done && 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30',
                active && 'bg-amber-500 text-white ring-4 ring-amber-100 shadow-lg shadow-amber-500/30',
                !done && !active && 'bg-slate-100 text-slate-400 ring-1 ring-slate-200',
              ].filter(Boolean).join(' ')}
            >
              {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : i + 1}
            </span>
            
            {/* Step label */}
            <span className={[
              'mt-2 text-[10.5px] text-center leading-tight font-medium transition-colors duration-300',
              done ? 'text-slate-700 dark:text-slate-300' : 
              active ? 'text-amber-700 font-semibold' : 
              'text-slate-400',
            ].join(' ')}>
              {s}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ShipmentMilestones = ({ maxItems = 5 }) => {
  const [fromDate] = useState(getMonthsAgoForInput(3));
  const [toDate] = useState(getTodayForInput());

  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white flex items-center justify-center shadow-lg shadow-blue-500/25">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-slate-900 dark:text-white">
              Shipment Milestones
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Track your shipments in real-time
            </p>
          </div>
        </div>
        
        <div className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-[12px] text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800">
          <Calendar className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
          {formatDisplayDate(fromDate)} — {formatDisplayDate(toDate)}
        </div>
      </div>

      {/* Shipments List */}
      <div className="p-5 space-y-5">
        {SAMPLE_SHIPMENTS.slice(0, maxItems).map((shipment, index) => (
          <motion.div
            key={shipment.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:shadow-lg transition-all duration-300 p-4"
          >
            {/* Shipment Header */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold text-slate-900 dark:text-white font-mono tracking-tight">
                    {shipment.id}
                  </span>
                  <span className={['inline-flex items-center px-2 h-5 rounded-md text-[10.5px] font-semibold', shipment.statusTone].join(' ')}>
                    {shipment.status}
                  </span>
                </div>
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2">
                  <Ship className="w-3 h-3" />
                  <span>{shipment.vessel}</span>
                  <span className="w-1 h-1 rounded-full bg-slate-300" />
                  <MapPin className="w-3 h-3" />
                  <span>{shipment.origin} → {shipment.destination}</span>
                </div>
              </div>
              
              {/* ETA & Last Update */}
              <div className="text-right text-[10px] text-slate-400 dark:text-slate-500">
                <div>ETA: {shipment.eta}</div>
                <div>Updated {shipment.lastUpdate}</div>
              </div>
            </div>
            
            {/* Milestone Track */}
            <MilestoneTrack current={shipment.currentStep} />
            
            {/* Progress Summary */}
            {/* <div className="mt-4 pt-3 flex items-center justify-between text-[10px] border-t border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-slate-500 dark:text-slate-400">Completed</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-slate-500 dark:text-slate-400">Current</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-slate-200 dark:bg-slate-600" />
                  <span className="text-slate-500 dark:text-slate-400">Pending</span>
                </div>
              </div>
              <div className="font-medium text-slate-600 dark:text-slate-300">
                Step {shipment.currentStep} of {STEPS.length}
              </div>
            </div> */}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default ShipmentMilestones;